import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import type Extent from "@arcgis/core/geometry/Extent";
import type {FL} from "../types/esri-extensions.tsx";
import Query from "@arcgis/core/rest/support/Query";

import FeatureSet from "@arcgis/core/rest/support/FeatureSet";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import FieldNames from "../defaults/FieldNames.json";
import type Graphic from '@arcgis/core/Graphic';
import PlannedActualsService from '../services/PlannedActualsService';
import esriRequest from '@arcgis/core/request';

export type ActionConfig = {
    title: string;
    id: string;
    className: string;
    disabled?: boolean;
}

export function initializeWebMapView(webMapId: string | null, container: HTMLDivElement | string, extent: null | Extent,): MapView {
    const map = webMapId ? new WebMap({portalItem: {id: webMapId}}) : new WebMap({basemap: 'hybrid'});
    const mapView = new MapView({
        map,
        container: container,
        extent: (extent) ? extent : {
            spatialReference: {
                wkid: 102100,
            },
            xmax: -7352956.75,
            xmin: -25374973.53,
            ymax: 11512895.13,
            ymin: -2634681.55,
        },

    });

    if (extent) {
        mapView.extent = extent;
    }
    return mapView;
}


export async function getLayerFromMapByPortalIdAndSetInitState(webmap: __esri.WebMap, portalId: string): Promise<FL> {
    const fl = webmap.allLayers.find(l => 'portalItem' in l && l instanceof FeatureLayer && l.portalItem?.id === portalId) as FL;
    if (!fl) {
        throw new Error(`Layer with portal ID ${portalId} not found in the map.`);
    }
    await fl.load();
    fl._initState = {
        definitionExpression: fl.definitionExpression,
        visible: fl?.visible,
    }

    return fl;
}


export function flattenToAttributes(results: __esri.FeatureSet[]) {
    return results.flat()
        .map(r => r?.features.map(f => f.attributes))
        .flat();
}


export function buildQuery(layer: FL, where: string, outFields: string[], geometry: __esri.Geometry | null, num: number, startIndex: number, sortValue: string): __esri.Query {
    const baseQuery = new Query({
        returnGeometry: false,
        outFields: outFields ? outFields : ['*'],
        spatialRelationship: "intersects",
        start: startIndex,
        num: num,
        where: where && layer._initState?.definitionExpression ? `(${where}) AND (${layer._initState.definitionExpression})` : where,
    })

    if (geometry) {
        baseQuery.geometry = geometry;
    }

    return baseQuery;
}

export function buildCountQuery(layer: FL, where: string, geometry: __esri.Geometry | null): __esri.Query {
    return new Query({
        returnGeometry: false,
        spatialRelationship: "intersects",
        where: where && layer._initState?.definitionExpression ? `(${where}) AND (${layer._initState.definitionExpression})` : where,
        geometry: geometry ? geometry : undefined,
    })
}

export async function queryAllFeatures(query: Query, layer: FL | FeatureLayer, sortByValue: string,  controller: AbortController): Promise<FeatureSet> {

    // use the count from the filter count dropdown for now, everything we will refactor this to use pagination in a future PR
    layer.definitionExpression = query.where;
    const ids = await layer.queryObjectIds(query, {signal: controller?.signal});
    if (ids.length > 0) {
        const idQuery = new Query({
            objectIds: ids,
            orderByFields: query.orderByFields,
            returnGeometry: false,
            outFields: query.outFields
        });
        if(sortByValue){
            setSortBy(sortByValue, idQuery);
        }
        return layer.queryFeatures(idQuery, {signal: controller?.signal});
    } else {
        // empty featureset
        return new FeatureSet();
    }
}

export async function queryFeatureCount(query: Query, layer: FL | FeatureLayer, controller: AbortController): Promise<number> {
    return layer.queryFeatureCount(query, {signal: controller?.signal});
}

export function setSortBy(sortByValue: string, featureQueryObject: Query) {
    if (sortByValue) {
        if (sortByValue.includes("FY")) {
            //How to do this?
            let descAsc = "";
            if (sortByValue.includes("ASC")) {
                descAsc = "ASC"
            } else if (sortByValue.includes("DESC")) {
                descAsc = "DESC"
            }
            featureQueryObject.orderByFields = [`(CASE WHEN CompletionFiscalYear IS NULL THEN InitiationFiscalYear ELSE CompletionFiscalYear END) ${descAsc}`];
        } else {
            featureQueryObject.orderByFields = [sortByValue];
        }
    }
}

export function configureActions(layer: FL | null, actionConfigs: ActionConfig[]) {
    if(!layer) {
        return;
    }
    if (!layer.popupTemplate) {
        layer.popupTemplate = new PopupTemplate();
    }
    layer.popupTemplate.overwriteActions = true;
    layer.popupTemplate.actions = actionConfigs as any;
}


export async function getLayerFromMapByUrl(webmap: __esri.WebMap, url: string): Promise<FeatureLayer | null> {
    const [u, idx] = splitLayerUrl(url);
    const fl = webmap.allLayers.find(l => l instanceof FeatureLayer && l.url != null && l.url.startsWith(u) && l.layerId === parseInt(idx, 10)) as FL;
    if (!fl) {
        // todo change to error service logger
        console.warn(`Layer with url ${url} not found in the map.`);
        return null
    }
    await fl.load().catch(e => console.warn(`Error loading layer with url ${url}:`, e));
    fl._initState = {
        definitionExpression: fl.definitionExpression,
        visible: fl?.visible,
    }

    return fl;
}

function splitLayerUrl(url: string) {
    const re = /FeatureServer\/|MapServer\//;
    return url.split(re);
}

export const estimatedTreatmentActions = [
    {
        title: FieldNames.layerPopup.treatmentView,
        id: FieldNames.layerPopup.treatmentViewId,
        className: "esri-icon-review"
    },
    {
        title: "Zoom To",
        id: FieldNames.layerPopup.treatmentZoomId,
        className: "esri-icon-zoom-in-magnifying-glass"
    },
];

export const actualTreatmentActions = [
    {
        title: FieldNames.layerPopup.treatmentView,
        id: FieldNames.layerPopup.actualTreatmentViewId,
        className: "esri-icon-review"
    },
    {
        title: "Zoom To",
        id: FieldNames.layerPopup.actualTreatmentZoomId,
        className: "esri-icon-zoom-in-magnifying-glass"
    },
];

export const estimatedActivityActions = [
    {
        title: FieldNames.layerPopup.activityView,
        id: FieldNames.layerPopup.activityViewId,
        className: "esri-icon-review"
    },
    {
        title: "Zoom To",
        id: FieldNames.layerPopup.activityZoomId,
        className: "esri-icon-zoom-in-magnifying-glass"
    },
];
export const actualActivityActions = [
    {
        title: FieldNames.layerPopup.activityView,
        id: FieldNames.layerPopup.activityViewId,
        className: "esri-icon-review"
    },
    {
        title: "Zoom To",
        id: FieldNames.layerPopup.actualActivityZoomId,
        className: "esri-icon-zoom-in-magnifying-glass"
    },
];
export const projectActions = [
    {
        title: FieldNames.layerPopup.projectView,
        id: FieldNames.layerPopup.projectViewId,
        className: "esri-icon-review"
    },
    {
        title: "Zoom To",
        id: FieldNames.layerPopup.projectZoomId,
        className: "esri-icon-zoom-in-magnifying-glass"
    },
];

export const additionalPolygonActions = [
    {
        title: FieldNames.layerPopup.copyTitle,
        id: "copy-polygon",
        className: "esri-icon-feature-layer",
        disabled: true
    }
];

export async function loadRelatedActualTreatments(url: string, joinId: number, joinFeature: string): Promise<Graphic[]> {
  const whereClause = `(${FieldNames.estimatedActuals.joinIDE} = ${joinId}) AND (${FieldNames.estimatedActuals.joinFeatureE} = '${joinFeature}') AND (IsArchived = 'No' OR IsArchived IS NULL)`;
  const records = await PlannedActualsService.getAllPlannedActualRecords(whereClause);
  console.debug('ProgressiveReportingList - loadData - records:', records);
  if (records.length === 0) {
    return [];
  }
  const joinIds = records.map((r: Graphic) => r.attributes.JoinIDA).join(',');

  return await esriRequest(`${url}/query`, {
    query: {
      where: `JoinID in (${joinIds}) AND JoinFeature = '${joinFeature}' AND (IsArchived = 'No' OR IsArchived IS NULL)`,
      outFields: ['*'],
      orderByFields: 'InitiationDate',
      f: 'json'
    }
  }).then((r: any) => {
    console.debug('ProgressiveReportingList - loadData - actual records:', r);
    return r.data.features ?? [];
  });
}
